// ============================================================================
// CHANGO V5.0 — Dirty Form Tracker (§9.5.1 客户端自动刷新决策)
// ============================================================================
// 全局 dirty form 状态注册中心。当 ops 后台改了客户的套餐 / 公司名后,
// `useLayersWatcher` 检测到 layers_revision 变化, 决定要不要立即刷新页面:
//   - 如果当前没有任何 dirty form  → 3 秒后自动 location.reload()
//   - 如果有任何 dirty form        → 弹 toast 让用户挑时机点"立即刷新"
//
// 所有有未保存表单的页面都应该在 form 内容变化时调 useDirtyForm() 注册:
//
//   function MyEditPage() {
//     const [draft, setDraft] = useState({ ... });
//     useDirtyForm('my-edit-page', () => isDirty(draft));
//     return <form>...</form>;
//   }
//
// useDirtyForm 在组件卸载时自动注销, 不需要手动清理。
// ============================================================================

import { useEffect } from 'react';

// 模块级单例: { [pageKey: string]: () => boolean }
const registry = new Map();

/**
 * 注册一个 dirty form 状态探针。pageKey 同一页 mount 多次会覆盖。
 * @param {string} pageKey  — 唯一标识本页的 key (路径 / 业务名等)
 * @param {() => boolean} isDirty  — 返回 true 表示当前有未保存修改
 */
export function useDirtyForm(pageKey, isDirty) {
  useEffect(() => {
    registry.set(pageKey, isDirty);
    return () => {
      registry.delete(pageKey);
    };
  }, [pageKey, isDirty]);
}

/**
 * 检查全站当前是否有任何 dirty form。
 * @returns {boolean}
 */
export function hasAnyDirtyForm() {
  for (const probe of registry.values()) {
    try {
      if (probe()) return true;
    } catch {
      // 探针抛错按"不脏"处理
    }
  }
  return false;
}

/**
 * Hook 形式: 当 dirty form 状态变化时返回当前布尔值。
 * 不强制订阅, 仅作 single-source-of-truth 暴露。
 */
export function useAnyDirtyForm() {
  // 不订阅模块级 Map 的变化 (没必要; 探针本身就是闭包, 调用即时计算)
  // 调用方需要在自己 setInterval 时机内调 hasAnyDirtyForm()
  return hasAnyDirtyForm();
}

export default { useDirtyForm, hasAnyDirtyForm, useAnyDirtyForm };
